import { dateToStr, getCompleted, todayStr } from './daily-record.js';

export interface DailyCalendarOptions {
  /** Variant id (e.g. 'klondike') - scopes the completion record. */
  variant: string;
  /** Called with a YYYY-MM-DD date when the player picks a day to play. */
  onPlay(date: string): void;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export interface DailyCalendar {
  open(): void;
  close(): void;
}

export function createDailyCalendar(opts: DailyCalendarOptions): DailyCalendar {
  let viewYear = 0;
  let viewMonth = 0; // 0-based
  let root: HTMLDivElement | null = null;

  function onKey(e: KeyboardEvent) {
    if (e.key === 'Escape') close();
  }

  function close() {
    if (!root) return;
    const el = root;
    root = null;
    el.classList.remove('is-open');
    document.removeEventListener('keydown', onKey);
    window.setTimeout(() => el.remove(), 200);
  }

  function open() {
    const now = new Date();
    viewYear = now.getFullYear();
    viewMonth = now.getMonth();
    if (!root) {
      root = document.createElement('div');
      root.className = 'daily-cal';
      root.addEventListener('click', (e) => {
        if (e.target === root) close(); // click the backdrop
      });
      document.body.appendChild(root);
      document.addEventListener('keydown', onKey);
      requestAnimationFrame(() => root?.classList.add('is-open'));
    }
    render();
  }

  function render() {
    if (!root) return;
    root.innerHTML = panelHTML();
    wire();
  }

  function panelHTML(): string {
    const completed = getCompleted(opts.variant);
    const today = todayStr();
    const now = new Date();
    const isCurrentMonth = viewYear === now.getFullYear() && viewMonth === now.getMonth();

    const startDow = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

    let cells = '';
    for (let i = 0; i < startDow; i++) {
      cells += '<span class="daily-cal__cell daily-cal__cell--empty"></span>';
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const ds = dateToStr(new Date(viewYear, viewMonth, d));
      const future = ds > today;
      const done = completed.has(ds);
      const cls = [
        'daily-cal__cell',
        future ? 'is-future' : 'is-playable',
        done ? 'is-done' : '',
        ds === today ? 'is-today' : '',
      ].filter(Boolean).join(' ');
      const check = done ? '<span class="daily-cal__check">✓</span>' : '';
      cells += `<button class="${cls}" type="button" data-date="${ds}"${future ? ' disabled' : ''}>${d}${check}</button>`;
    }

    const dow = WEEKDAYS.map((w) => `<span class="daily-cal__dow">${w}</span>`).join('');
    const total = completed.size;

    return `
      <div class="daily-cal__panel" role="dialog" aria-modal="true" aria-label="Daily deals">
        <div class="daily-cal__head">
          <button class="daily-cal__nav" type="button" data-nav="-1" aria-label="Previous month">&lsaquo;</button>
          <span class="daily-cal__title">${MONTHS[viewMonth]} ${viewYear}</span>
          <button class="daily-cal__nav" type="button" data-nav="1" aria-label="Next month"${isCurrentMonth ? ' disabled' : ''}>&rsaquo;</button>
          <button class="daily-cal__close" type="button" data-close aria-label="Close">&times;</button>
        </div>
        <div class="daily-cal__grid daily-cal__grid--dow">${dow}</div>
        <div class="daily-cal__grid">${cells}</div>
        <div class="daily-cal__foot">
          <span class="daily-cal__count">🏆 ${total} daily win${total === 1 ? '' : 's'}</span>
          <span class="daily-cal__hint">Pick a day to play its deal</span>
        </div>
      </div>`;
  }

  function wire() {
    if (!root) return;
    root.querySelector('[data-close]')?.addEventListener('click', close);
    root.querySelectorAll<HTMLElement>('[data-nav]').forEach((btn) => {
      btn.addEventListener('click', () => {
        viewMonth += Number(btn.dataset.nav);
        if (viewMonth < 0) {
          viewMonth = 11;
          viewYear--;
        } else if (viewMonth > 11) {
          viewMonth = 0;
          viewYear++;
        }
        render();
      });
    });
    root.querySelectorAll<HTMLElement>('[data-date]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const date = btn.dataset.date!;
        close();
        opts.onPlay(date);
      });
    });
  }

  return { open, close };
}
